"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CometChatAPI = void 0;
const URI_1 = require("lincd/lib/utils/URI");
const User_1 = require("./User");
const chalk_1 = __importDefault(require("chalk"));
class CometChatAPI {
    //We need this line to force the import of CometChat to stay in the js and .d.ts files
    //so that the typescript types of CometChat actually work when this package is used (this is an issue on CometChats' side)
    // static api: CometChat = CometChat as any;
    static isConfigured() {
        return (process.env.COMET_CHAT_API_KEY && process.env.COMET_CHAT_APP_ID && true);
    }
    static createGroup(groupName, ownerID = process.env.COMET_CHAT_ADMIN_ID, icon = '', participantIDs = []) {
        let guid = URI_1.URI.sanitize(groupName);
        const options = {
            method: 'POST',
            headers: {
                accept: 'application/json',
                onBehalfOf: ownerID,
                'content-type': 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
            body: JSON.stringify({
                guid: guid,
                name: groupName,
                type: 'public',
                icon: icon,
                owner: ownerID,
                members: {
                    participants: participantIDs,
                },
                // Add other properties as needed (e.g., password, icon, description, etc.)
            }),
        };
        return fetch(`https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/groups`, options)
            .then((response) => response.json())
            .then((response) => {
            if (response) {
                if (response.error) {
                    if (response.error.code === 'ERR_GUID_ALREADY_EXISTS') {
                        console.log('Group already exists:', groupName);
                        return null;
                        //TODO: create and return this.getGroup(guid)
                    }
                    else {
                        console.error(`Error creating group ${guid}: ${JSON.stringify(response, null, 2)}`);
                    }
                }
                else {
                    return response;
                }
            }
        })
            .catch((err) => {
            console.error('Error during cometchat group creation: ' + err.toString());
            return null;
        });
    }
    static addAccountToGroup(userAccount, groupID) {
        const userUID = (0, User_1.getUID)(userAccount.accountOf);
        if (!userUID) {
            console.warn('Could not generate userUID for this person:' +
                userAccount.accountOf.toString());
            return Promise.reject();
        }
        const url = `https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/groups/${groupID}/members`;
        const options = {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'content-type': 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
            body: JSON.stringify({
                // admins: ['string'],
                // moderators: ['string'],
                participants: [userUID],
                // usersToBan: ['string'],
            }),
        };
        return fetch(url, options)
            .then((res) => res.json())
            .then((response) => {
            if (response.error) {
                console.error('Error adding user to group:', JSON.stringify(response, null, 2));
                return response;
            }
            else {
                for (let uid in response.data.participants) {
                    let userRes = response.data.participants[uid];
                    let err = userRes.error;
                    if (err) {
                        if (err.code === 'ERR_SAME_SCOPE') {
                            console.log(`CometChat: User ${uid} is already a member of group ${groupID}`);
                        }
                        else {
                            console.warn(`CometChat: Could not add user ${uid} to group ${groupID}: ${err.code} - ${err.message}`);
                        }
                    }
                    else if (userRes.success) {
                        console.log(`CometChat: Added user ${uid} to group ${groupID}`);
                    }
                }
                return response;
            }
        })
            .catch((error) => {
            console.error('Error adding user to group:', error.code, error.message);
        });
    }
    static createUserFromAccount(userAccount) {
        let userBody = (0, User_1.toCometChatUserData)(userAccount);
        if (!process.env.COMET_CHAT_APP_ID) {
            if (!global['cometchatUserCreationSkipped']) {
                global['cometchatUserCreationSkipped'] = true;
                console.log(chalk_1.default.magenta('Skipping CometChat user creation, no app id set'));
            }
            return;
        }
        const options = {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'content-type': 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
            body: JSON.stringify(userBody),
        };
        return fetch(`https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/users`, options)
            .then((response) => response.json())
            .then((response) => {
            if (response.error) {
                if (response.error.code === 'ERR_UID_ALREADY_EXISTS') {
                    console.log('CometChat: User already exists:', userBody.uid);
                    return null;
                }
                else {
                    console.log('CometChat: Error creating user: ' +
                        JSON.stringify(response, null, 2));
                }
                // console.error(JSON.stringify(response, null, 2));
            }
            else {
                console.log('CometChat: Created new user ' + userBody.uid);
                return response.data;
            }
        })
            .catch((err) => {
            if (err.code === 'ERR_UID_ALREADY_EXISTS') {
                console.log('User already exists:', userBody.guid);
                return null;
            }
            else {
                console.error('Error creating cometchat user: ' +
                    err +
                    `. Requested https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/users -> ` +
                    JSON.stringify(userBody, null, 2));
            }
        });
    }
    static removeUserFromAccount(account) {
        let uid = (0, User_1.getUID)(account.accountOf);
        const options = {
            method: 'DELETE',
            headers: {
                accept: 'application/json',
                'content-type': 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
            body: JSON.stringify({ permanent: true }),
        };
        return fetch(`https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/users/${uid}`, options)
            .then((response) => response.json())
            .then((response) => {
            if (response.error) {
                console.error('Could not remove user from cometchat: ' +
                    JSON.stringify(response, null, 2));
                return false;
            }
            return true;
        })
            .catch((err) => {
            console.error('Could not remove user from cometchat: ' + err);
        });
    }
    static getUserFromAccount(account) {
        let uid = (0, User_1.getUID)(account.accountOf);
        const options = {
            method: 'GET',
            headers: {
                accept: 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
        };
        return fetch(`https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/users/${uid}`, options)
            .then((response) => response.json())
            .then((response) => {
            return response;
        })
            .catch((err) => {
            console.error('Error fetching user ' + uid + ': ' + err);
        });
    }
    static getUsersList(perPage = 100, page = 1) {
        const options = {
            method: 'GET',
            headers: {
                accept: 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
        };
        const params = `?perPage=${perPage}&page=${page}`;
        const url = `https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/users${params}`;
        return fetch(url, options)
            .then((response) => response.json())
            .then((response) => {
            return response;
        })
            .catch((err) => {
            console.error('Error fetching users list: ' + err);
        });
    }
    static updateUserFromAccount(account) {
        const uid = (0, User_1.getUID)(account.accountOf);
        const options = {
            method: 'PUT',
            headers: {
                accept: 'application/json',
                'content-type': 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
            apikey: process.env.COMET_CHAT_API_KEY,
            body: JSON.stringify((0, User_1.toCometChatUserData)(account)),
        };
        const url = `https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/users/${uid}`;
        return fetch(url, options)
            .then((response) => response.json())
            .then((response) => {
            return response;
        })
            .catch((err) => {
            console.error('Error update user: ' + err);
        });
    }
    static getGroup(guid) {
        const options = {
            method: 'GET',
            headers: {
                accept: 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
        };
        return fetch(`https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/groups/${guid}`, options)
            .then((response) => response.json())
            .then((response) => {
            return response;
        })
            .catch((err) => {
            console.error('Error fetching group ' + guid + ': ' + err);
        });
    }
    static getGroupMembers(guid) {
        const options = {
            method: 'GET',
            headers: {
                accept: 'application/json',
                apikey: process.env.COMET_CHAT_API_KEY,
            },
        };
        return fetch(`https://${process.env.COMET_CHAT_APP_ID}.api-us.cometchat.io/v3/groups/${guid}/members`, options)
            .then((response) => response.json())
            .then((response) => {
            return response;
        })
            .catch((err) => {
            console.error('Error fetching group members ' + guid + ': ' + err);
        });
    }
}
exports.CometChatAPI = CometChatAPI;
//# sourceMappingURL=CometChatAPI.js.map